/**
 * The plain change (spec 028): what one assistant step changed, in words an
 * author would use ("Dana made bigger"), with no ids, keys, units or raw
 * values. Pure: each describer compares the document before and after the
 * step. The Service sends these with every turn step; the editor groups them
 * into the Changes report (`groupChanges`).
 *
 * Every operation of 021's scene registry and 023's story registry (026's
 * picture operations included) has a describer; a test enumerates both.
 */
import type { Orientation, Sprite, SpriteState, StatePlayback, StoredPictureMap } from '../@types';
import { isSpritePictureKind, pictureKindPhrase, syncPictureMap } from '../pictures/pictureMap';
import { pictureStoryOf } from './scenePictures';
import { getBasisOrientation, getResponsivePosition, getResponsiveTransform } from '../utils/responsiveLayout';
import { backToFront } from './placement';
import { findSprite } from './getSprite';
import { keyframeValueAt } from '../utils/keyframeWrite';
import { secToMs } from './units';
import { isHeldScene } from './operations';
import type { SceneDocument } from './types';
import { playbackFromArg } from './playbackArg';
import type { StoryDocScene, StoryDocument } from '../storyOperations/types';

export interface PlainChange {
  /** Story scene key (ss-<id> | new-<id>); absent in the scene assistant and for story-wide changes. */
  scene?: string;
  /** The scene's name after the step. */
  sceneName?: string;
  /** The sprite (character) names it is about, by name, never id; [] for scene or story things. */
  who: string[];
  /** Plain past-tense predicate: no ids, keys, units or raw values. */
  text: string;
  /** Set when the step was for one orientation only. */
  orientation?: Orientation;
  /** Something the author should look at (a connection left with no choice). */
  warn?: true;
  /** The scene is shared with other stories (023), so they change too. */
  shared?: true;
  /**
   * Never shown: the element the change is about (a sprite id), so a turn's
   * grouping can tell that a sprite added this turn was then changed.
   */
  target?: string;
  /**
   * Never shown: the changed property's value before and after this step, so
   * a turn's grouping can net out a change a later step undid.
   */
  net?: { key: string; from: unknown; to: unknown };
}

// ─── Helpers ────────────────────────────────────────────────────────────────

const same = (a: unknown, b: unknown): boolean => JSON.stringify(a ?? null) === JSON.stringify(b ?? null);
const spriteIn = (doc: SceneDocument, id: unknown): Sprite | undefined =>
  typeof id === 'string' ? (doc.sprites ?? []).find((s) => s.id === id) : undefined;
const basisOf = (sprite: Sprite): Orientation => getBasisOrientation(sprite.layout) ?? 'portrait';
/** A sprite's keyframe track in a document (spec 021 v15: motion is keyframes). */
const trackIn = (doc: SceneDocument, id: unknown) => (doc.keyframes ?? []).find((t) => t.trackId === id)?.keyframes;
const scoped = (o: unknown): { orientation?: Orientation } =>
  (o === 'portrait' || o === 'landscape' || o === 'desktop' ? { orientation: o } : {});

/** Spec 036: the sprite a keyframe operation named (`sprite`, an id or a name), when its keys changed. */
function keyedSprite(args: { sprite?: unknown }, before: SceneDocument, after: SceneDocument): Sprite | undefined {
  if (typeof args.sprite !== 'string') return undefined;
  const found = findSprite(before, args.sprite);
  if (found.ok === false || same(trackIn(before, found.sprite.id), trackIn(after, found.sprite.id))) return undefined;
  return found.sprite;
}

const about = (sprite: Sprite, text: string, extra: Partial<PlainChange> = {}): PlainChange =>
  ({ who: [sprite.name], text, target: sprite.id, ...extra });
const thing = (text: string): PlainChange => ({ who: [], text });

// eslint-disable-next-line @typescript-eslint/no-explicit-any -- each describer reads its own operation's args
type Args = any;
type SceneDescriber = (args: Args, before: SceneDocument, after: SceneDocument) => PlainChange[];
type StoryDescriber = (args: Args, before: StoryDocument, after: StoryDocument) => PlainChange[];

function transformChange(field: 'scale' | 'rotation', s0: Sprite, s1: Sprite, o: unknown): PlainChange | undefined {
  const at: Orientation = (scoped(o).orientation) ?? basisOf(s1);
  const v0 = getResponsiveTransform(s0.layout, at, s0.transform)[field];
  const v1 = getResponsiveTransform(s1.layout, at, s1.transform)[field];
  if (v0 === v1) return undefined;
  const text = field === 'rotation' ? 'turned' : v1 > v0 ? 'made bigger' : 'made smaller';
  return about(s1, text, { ...scoped(o), net: { key: `${s1.id}:${field}:${at}`, from: v0, to: v1 } });
}

const MODE_WORDS: Record<StatePlayback['mode'], string> = {
  loop: 'on a loop', once: 'once', pingpong: 'back and forth', still: 'still',
};
const speedNumber = (v: number) => `${Number(v.toFixed(2))}×`;

/**
 * Spec 035: how a state's playback changed, in the studio's words, after the
 * sprite's name: "'s talking now plays once, faster (1.5×)", "'s wave now
 * plays once, then goes back to its first frame" (never a repeat count or a
 * following state, spec 033 D-7). Undefined when nothing a person would notice changed.
 */
export function playbackChangeText(state: string, before: StatePlayback | undefined, after: StatePlayback | undefined): string | undefined {
  if (same(before, after)) return undefined;
  const p0 = before ?? { mode: 'loop' as const };
  const p1 = after ?? { mode: 'loop' as const };
  if (p1.mode === 'still') return p0.mode === 'still' ? undefined : `'s ${state} is now still`;
  const parts: string[] = [];
  if (p1.mode !== p0.mode) parts.push(MODE_WORDS[p1.mode]);
  const s0 = p0.speed ?? 1;
  const s1 = p1.speed ?? 1;
  if (s1 !== s0) parts.push(s1 === 1 ? 'at its own speed' : `${s1 > s0 ? 'faster' : 'slower'} (${speedNumber(s1)})`);
  if (!same(p1.range, p0.range)) parts.push(p1.range ? `frames ${p1.range[0] + 1} to ${p1.range[1] + 1}` : 'every frame');
  if (!!p1.randomStart !== !!p0.randomStart) parts.push(p1.randomStart ? 'from its own frame' : 'from the first frame');
  if (!same(p1.whenDone, p0.whenDone)) parts.push(p1.whenDone === 'hold-first' ? 'then goes back to its first frame' : 'then holds its last frame');
  return parts.length ? `'s ${state} now plays ${parts.join(', ')}` : undefined;
}

const withoutPlayback = (states: SpriteState[]) => states.map(({ playback: _p, ...rest }) => rest);

// ─── Scene operations (021) ─────────────────────────────────────────────────

const SCENE_DESCRIBERS: Readonly<Record<string, SceneDescriber>> = {
  addSprite(_args, before, after) {
    const known = new Set((before.sprites ?? []).map((s) => s.id));
    return (after.sprites ?? []).filter((s) => !known.has(s.id)).map((s) => about(s, 'added'));
  },

  editSprite(args, before, after) {
    const s0 = spriteIn(before, args.spriteId);
    const s1 = spriteIn(after, args.spriteId);
    if (!s0 || !s1) return [];
    const out: PlainChange[] = [];
    if (s0.name !== s1.name) out.push({ who: [s0.name], text: `renamed to ${s1.name}`, target: s1.id });
    if (!same(s0.size, s1.size)) out.push(about(s1, 'resized', { net: { key: `${s1.id}:size`, from: s0.size, to: s1.size } }));
    if (!same(s0.hinge, s1.hinge)) out.push(about(s1, 'pivot moved', { net: { key: `${s1.id}:hinge`, from: s0.hinge, to: s1.hinge } }));
    if (args.scale !== undefined) {
      const c = transformChange('scale', s0, s1, args.orientation);
      if (c) out.push(c);
    }
    if (args.rotation !== undefined) {
      const c = transformChange('rotation', s0, s1, args.orientation);
      if (c) out.push(c);
    }
    for (const axis of ['flipX', 'flipY'] as const) {
      if (args[axis] === undefined) continue;
      const was = !!getResponsiveTransform(s0.layout, basisOf(s1), s0.transform)[axis];
      if (was !== !!args[axis]) out.push(about(s1, args[axis] ? 'mirrored' : 'shown as drawn'));
    }
    if (!same(withoutPlayback(s0.states), withoutPlayback(s1.states))) out.push(about(s1, 'expressions changed'));
    for (const st of s1.states) {
      const was = s0.states.find((x) => x.name === st.name);
      if (!was) continue;
      const text = playbackChangeText(st.name, was.playback, st.playback);
      if (text) out.push(about(s1, text));
    }
    if (s0.defaultState !== s1.defaultState) out.push(about(s1, `now looks ${s1.defaultState}`));
    return out;
  },

  placeSprite(args, before, after) {
    const s0 = spriteIn(before, args.spriteId);
    const s1 = spriteIn(after, args.spriteId);
    if (!s0 || !s1) return [];
    const out: PlainChange[] = [];
    if (args.placement !== undefined && !same(s0.layout, s1.layout)) {
      const at: Orientation = scoped(args.orientation).orientation ?? basisOf(s1);
      const p0 = getResponsivePosition(s0.layout, at);
      const p1 = getResponsivePosition(s1.layout, at);
      out.push(about(s1, 'moved', {
        ...scoped(args.orientation),
        net: { key: `${s1.id}:position:${at}`, from: { x: p0.x, y: p0.y }, to: { x: p1.x, y: p1.y } },
      }));
    }
    const order0 = backToFront(before.sprites ?? []).map((s) => s.id);
    const order1 = backToFront(after.sprites ?? []).map((s) => s.id);
    if (!same(order0, order1)) {
      const ref = spriteIn(after, args.inFrontOf ?? args.behind);
      let text: string;
      if (ref && args.inFrontOf !== undefined) text = `moved in front of ${ref.name}`;
      else if (ref) text = `moved behind ${ref.name}`;
      else text = order1.indexOf(s1.id) > order0.indexOf(s1.id) ? 'brought forward' : 'sent back';
      out.push(about(s1, text));
    }
    return out;
  },

  setSpriteMotion(args, before, after) {
    const s0 = spriteIn(before, args.spriteId);
    const s1 = spriteIn(after, args.spriteId);
    if (!s0 || !s1 || same(trackIn(before, args.spriteId), trackIn(after, args.spriteId))) return [];
    const o = scoped(args.orientation);
    const out: PlainChange[] = [];
    if (args.enter !== undefined) out.push(about(s1, 'entrance changed', o));
    if (args.exit !== undefined) out.push(about(s1, 'exit changed', o));
    return out;
  },

  // Spec 036: the keyframe operations, the sprite named by id or name.
  moveSprite(args, before, after) {
    const sprite = keyedSprite(args, before, after);
    if (!sprite) return [];
    const to = (args.to ?? {}) as Record<string, number>;
    const at: Orientation = scoped(args.orientation).orientation ?? 'desktop';
    const words: string[] = [];
    if (to.x !== undefined || to.y !== undefined) words.push('moved');
    if (to.scale !== undefined) {
      const was = keyframeValueAt(sprite, trackIn(before, sprite.id), 'transform.scale', secToMs(Number(args.at)), at) as number;
      if (to.scale !== was) words.push(to.scale > was ? 'made bigger' : 'made smaller');
    }
    if (to.rotation !== undefined) words.push('turned');
    if (to.opacity !== undefined) words.push('faded');
    if (to.hingeX !== undefined || to.hingeY !== undefined) words.push('hinge moved');
    return words.length ? [about(sprite, words.join(' and '), scoped(args.orientation))] : [];
  },
  setSpriteState(args, before, after) {
    const sprite = keyedSprite(args, before, after);
    return sprite ? [about(sprite, `switches to ${args.state}`, scoped(args.orientation))] : [];
  },
  showSprite(args, before, after) {
    const sprite = keyedSprite(args, before, after);
    return sprite ? [about(sprite, 'appears', scoped(args.orientation))] : [];
  },
  hideSprite(args, before, after) {
    const sprite = keyedSprite(args, before, after);
    return sprite ? [about(sprite, 'leaves', scoped(args.orientation))] : [];
  },
  // Spec 038.
  flipSprite(args, before, after) {
    const sprite = keyedSprite(args, before, after);
    return sprite ? [about(sprite, 'turns round', scoped(args.orientation))] : [];
  },

  setDialog(args, before, after) {
    if (args.dialogId !== undefined) {
      const d0 = (before.dialogs ?? []).find((d) => d.id === args.dialogId);
      const d1 = (after.dialogs ?? []).find((d) => d.id === args.dialogId);
      return d0 && d1 && !same(d0, d1) ? [thing(`Dialog "${d1.name}" changed`)] : [];
    }
    const known = new Set((before.dialogs ?? []).map((d) => d.id));
    return (after.dialogs ?? []).filter((d) => !known.has(d.id)).map((d) => thing(`Dialog "${d.name}" added`));
  },

  setSound(args, before, after) {
    if (args.soundId !== undefined) {
      const s0 = (before.sounds ?? []).find((s) => s.id === args.soundId);
      const s1 = (after.sounds ?? []).find((s) => s.id === args.soundId);
      return s0 && s1 && !same(s0, s1) ? [thing(`Sound "${s1.name}" changed`)] : [];
    }
    const known = new Set((before.sounds ?? []).map((s) => s.id));
    return (after.sounds ?? []).filter((s) => !known.has(s.id)).map((s) => thing(`Sound "${s.name}" added`));
  },

  setBackground: (_args, before, after) => (same(before.background, after.background) ? [] : [thing('Background changed')]),

  // Spec 039: the background's states and its changes of place.
  setBackgroundStates: (_args, before, after) =>
    (same(before.background, after.background) && same(before.keyframes, after.keyframes) ? [] : [thing('Background states set')]),

  setBackgroundState: (args, before, after) =>
    (same(before.keyframes, after.keyframes) ? [] : [thing(`Background changes to ${String(args.state ?? '').trim()}`)]),

  setSceneTransitions: (_args, before, after) =>
    (same(before.enterTransition, after.enterTransition) && same(before.exitTransition, after.exitTransition)
      ? [] : [thing('Scene transitions changed')]),

  // Spec 041 (in 028's words: no number of seconds).
  setSceneLength(_args, before, after) {
    if (same(before.duration, after.duration)) return [];
    return [thing(isHeldScene(after) ? 'Scene held to a set length' : 'Scene follows its content')];
  },

  setChoices: (_args, before, after) =>
    (same(before.choices, after.choices) && same(before.choiceSettings, after.choiceSettings) ? [] : [thing('Choices changed')]),

  removeElement(args, before) {
    const sprite = spriteIn(before, args.id);
    if (sprite) return [about(sprite, 'removed')];
    const dialog = (before.dialogs ?? []).find((d) => d.id === args.id);
    if (dialog) return [thing(`Dialog "${dialog.name}" removed`)];
    const sound = (before.sounds ?? []).find((s) => s.id === args.id);
    if (sound) return [thing(`Sound "${sound.name}" removed`)];
    return [];
  },

  renameScene: (_args, before, after) => (before.name === after.name ? [] : [thing('Scene renamed')]),

  resetSpriteOrientation(args, before, after) {
    const s0 = spriteIn(before, args.spriteId);
    const s1 = spriteIn(after, args.spriteId);
    if (!s0 || !s1 || (same(s0, s1) && same(trackIn(before, args.spriteId), trackIn(after, args.spriteId)))) return [];
    return [about(s1, 'back to the shared layout', scoped(args.orientation))];
  },

  setLineExpression(args, before, after) {
    const d0 = (before.dialogs ?? []).find((d) => d.id === args.dialogId);
    const d1 = (after.dialogs ?? []).find((d) => d.id === args.dialogId);
    if (!d0 || !d1 || same(d0, d1)) return [];
    const index = Number(args.line) - 1;
    const speakerId = args.spriteId ?? d0.lines?.[index]?.expression?.spriteId;
    const speaker = spriteIn(after, speakerId) ?? spriteIn(before, speakerId);
    const text = `expression changed in "${d1.name}"`;
    return [speaker ? about(speaker, text) : thing(text)];
  },
};

// ─── Story operations (023, 026) ────────────────────────────────────────────

const sceneOf = (doc: StoryDocument, key: unknown): StoryDocScene | undefined =>
  (typeof key === 'string' ? doc.scenes.find((s) => s.key === key) : undefined);
const sceneName = (s: StoryDocScene): string => s.doc.name || s.name;
function nodeName(doc: StoryDocument, nodeId: string): string {
  const node = doc.nodes.find((n) => n.id === nodeId);
  const scene = node && sceneOf(doc, node.sceneKey);
  return scene ? sceneName(scene) : '';
}
const DANGLING = 'A connection from this scene now leads nowhere';

/** Who a picture is of: a character's name, nobody for a background or other. */
const pictureWho = (slot: { kind?: string; subject?: string } | undefined): string[] =>
  (slot && slot.kind === 'character' && slot.subject ? [slot.subject.trim()] : []);
const slotIn = (doc: StoryDocument, id: unknown) =>
  syncPictureMap(doc.pictureMap as StoredPictureMap, doc.scenes).slots.find((s) => s.id === id);

const STORY_DESCRIBERS: Readonly<Record<string, StoryDescriber>> = {
  createScene(_args, before, after) {
    const known = new Set(before.scenes.map((s) => s.key));
    return after.scenes.filter((s) => !known.has(s.key)).map((s) => ({ scene: s.key, sceneName: sceneName(s), who: [], text: 'New scene' }));
  },
  addNode(args, _before, after) {
    const s = sceneOf(after, args.scene);
    return s ? [{ scene: s.key, sceneName: sceneName(s), who: [], text: 'Added to the story map' }] : [];
  },
  removeNode: (args, before) => [thing(`"${nodeName(before, args.nodeId)}" taken off the story map`)],
  setStartNode: (args, before, after) =>
    (before.startNodeId === after.startNodeId ? [] : [thing(`The story now starts at "${nodeName(after, args.nodeId)}"`)]),
  connect(_args, before, after) {
    const known = new Set(before.edges.map((e) => e.id));
    return after.edges.filter((e) => !known.has(e.id))
      .map((e) => thing(`"${nodeName(after, e.from)}" now leads to "${nodeName(after, e.to)}"`));
  },
  disconnect(args, before) {
    const edge = before.edges.find((e) => e.id === args.edgeId);
    return edge ? [thing(`"${nodeName(before, edge.from)}" no longer leads to "${nodeName(before, edge.to)}"`)] : [];
  },
  setEdgeCondition(args, before, after) {
    const e0 = before.edges.find((e) => e.id === args.edgeId);
    const e1 = after.edges.find((e) => e.id === args.edgeId);
    if (!e1 || same(e0, e1)) return [];
    return [thing(`Changed when "${nodeName(after, e1.from)}" leads to "${nodeName(after, e1.to)}"`)];
  },
  arrangeGraph: () => [thing('Story map tidied')],
  renameStory: (_args, before, after) => (before.name === after.name ? [] : [thing(`Story renamed to "${after.name}"`)]),

  notePictureSlot: (args) => [{ who: pictureWho(args), text: 'picture noted' }],
  removePictureSlot: (args, before) =>
    [{ who: pictureWho(before.pictureMap.slots.find((s) => s.id === args.slot)), text: 'picture removed' }],
  fillPictureSlot: (args, before) => [{ who: pictureWho(slotIn(before, args.slot)), text: 'picture filled' }],
  setPictureFraming: (args) => [{ who: typeof args.subject === 'string' ? [args.subject.trim()] : [], text: 'framing changed' }],
  clearPictures: () => [thing('pictures discarded')],
  resetPictureSlot: (args, before) => [{ who: pictureWho(slotIn(before, args.slot)), text: 'picture reset' }],
  setPicturePlayback: (args, before) => {
    const slot = slotIn(before, args.slotId);
    const use = slot?.uses.find((u) => u.kind === 'sprite');
    const was = use && sceneOf(before, use.scene)?.doc.sprites?.find((sp) => sp.id === use.spriteId)?.states.find((st) => st.name === use.state);
    const text = slot && args.playback ? playbackChangeText(slot.variant, was?.playback, playbackFromArg(args.playback)) : undefined;
    return [{ who: pictureWho(slot), text: text ? `${text} everywhere` : 'playback changed' }];
  },
  // 026 D-26: the summary's words, `<subject> is now a prop`.
  setPictureKind: (args) => {
    const subject = typeof args.subject === 'string' ? args.subject.trim() : '';
    const kind = typeof args.kind === 'string' && isSpritePictureKind(args.kind) ? args.kind : undefined;
    return [thing(kind && subject ? `${subject} is now ${pictureKindPhrase(kind)}` : 'picture kind changed')];
  },
};

/** A wrapped 021 operation on one story scene: the scene's plain changes, keyed to it. */
function describeWrapped(name: string, args: Args, before: StoryDocument, after: StoryDocument): PlainChange[] {
  const { scene: key, ...rest } = (args ?? {}) as Record<string, unknown>;
  const s0 = sceneOf(before, key);
  const s1 = sceneOf(after, key);
  if (!s0 || !s1) return [];
  const changes = describeChange(name, rest, s0.doc, s1.doc);
  if (name === 'setChoices') {
    const nodeIds = new Set(after.nodes.filter((n) => n.sceneKey === s1.key).map((n) => n.id));
    const ids0 = new Set((s0.doc.choices ?? []).map((c) => c.id));
    const ids1 = new Set((s1.doc.choices ?? []).map((c) => c.id));
    for (const edge of after.edges) {
      if (!nodeIds.has(edge.from) || !edge.condition) continue;
      if (!ids1.has(edge.condition) && ids0.has(edge.condition)) changes.push({ who: [], text: DANGLING, warn: true });
    }
  }
  return changes.map((c) => ({
    ...c,
    scene: s1.key,
    sceneName: sceneName(s1),
    ...(s1.mode === 'reference' ? { shared: true as const } : {}),
  }));
}

/**
 * Spec 030: the six picture operations on a standalone scene's own map, in
 * the same words as their story rows (the character as `who`), read through
 * the one-scene story they run on.
 */
const SCENE_PICTURE_DESCRIBERS: Readonly<Record<string, SceneDescriber>> = Object.fromEntries(
  ['notePictureSlot', 'removePictureSlot', 'fillPictureSlot', 'setPictureFraming', 'clearPictures', 'resetPictureSlot', 'setPicturePlayback', 'setPictureKind']
    .map((name): [string, SceneDescriber] => [name, (args, before, after) => STORY_DESCRIBERS[name](args, pictureStoryOf(before), pictureStoryOf(after))]),
);

/** The operation names with a describer: 021's scene operations, then 023's story and 026's picture operations. */
export const PLAIN_CHANGE_SCENE_OPERATIONS: readonly string[] = Object.freeze(Object.keys(SCENE_DESCRIBERS));
/** Spec 030: the scene picture operations with a describer. */
export const PLAIN_CHANGE_SCENE_PICTURE_OPERATIONS: readonly string[] = Object.freeze(Object.keys(SCENE_PICTURE_DESCRIBERS));
export const PLAIN_CHANGE_STORY_OPERATIONS: readonly string[] = Object.freeze(Object.keys(STORY_DESCRIBERS));

/**
 * The plain changes of one 021 scene operation, from the scene document
 * before and after it. A step that changed nothing visible gives none.
 */
export function describeChange(name: string, args: unknown, before: SceneDocument, after: SceneDocument): PlainChange[] {
  if (before === after) return [];
  const describer = SCENE_DESCRIBERS[name] ?? SCENE_PICTURE_DESCRIBERS[name];
  return describer ? describer(args ?? {}, before, after) : [];
}

/**
 * The plain changes of one 023 story operation (a wrapped scene operation, a
 * story operation or one of 026's picture operations), from the story
 * document before and after it.
 */
export function describeStoryChange(name: string, args: unknown, before: StoryDocument, after: StoryDocument): PlainChange[] {
  if (before === after) return [];
  if (SCENE_DESCRIBERS[name]) return describeWrapped(name, args, before, after);
  const describer = STORY_DESCRIBERS[name];
  return describer ? describer(args ?? {}, before, after) : [];
}
